import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { Loader2, Zap, Target, Activity, Gauge, TrendingUp, Package } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, AreaChart, Area, BarChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell
} from 'recharts';

function formatIndian(num) {
  if (!num && num !== 0) return '-';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
}

function KPICard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="p-3 rounded-lg border transition-all hover:scale-[1.02]" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
      data-testid={`pwm-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[9px] font-medium uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</p>
          <p className="text-xl font-bold mt-0.5" style={{ color: color || 'var(--text-primary)' }}>{value}</p>
          {sub && <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</p>}
        </div>
        {Icon && <Icon className="w-4 h-4" style={{ color: color || 'var(--accent-primary)', opacity: 0.6 }} />}
      </div>
    </div>
  );
}

export default function PWMAnalytics({ projectId, filters }) {
  const { isDark } = useTheme();
  const [summary, setSummary] = useState(null);
  const [fabKpis, setFabKpis] = useState(null);
  const [erecKpis, setErecKpis] = useState(null);
  const [sCurve, setSCurve] = useState([]);
  const [erecSCurve, setErecSCurve] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, fabRes, erecRes, curveRes, eCurveRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/pwm/summary`),
        api.get(`/v1/projects/${projectId}/pwm/kpis/fabrication`),
        api.get(`/v1/projects/${projectId}/pwm/kpis/erection`),
        api.get(`/v1/projects/${projectId}/pwm/summary/s-curve?metric=fabrication&interval=weekly`),
        api.get(`/v1/projects/${projectId}/pwm/summary/s-curve?metric=erection&interval=weekly`),
      ]);
      setSummary(sumRes.data);
      setFabKpis(fabRes.data);
      setErecKpis(erecRes.data);
      setSCurve(curveRes.data?.data || []);
      setErecSCurve(eCurveRes.data?.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const gridColor = isDark ? '#1e293b' : '#e2e8f0';
  const tickColor = isDark ? '#64748b' : '#94a3b8';

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;
  if (!summary) return <div className="text-center py-16 text-sm" style={{ color: 'var(--text-muted)' }}>No data available.</div>;

  const ag = summary.ag || {};
  const ug = summary.ug || {};

  // State distribution for pie
  const stateData = Object.entries(summary.allocationStateDistribution || {}).map(([k, v]) => ({
    name: k.replace(/_/g, ' '), value: v,
    fill: { UNALLOCATED: '#ef4444', PARTIAL_SOFT: '#f59e0b', READY_SOFT: '#3b82f6', PO_READY: '#143A6B', PARTIAL_HARD: '#eab308', FULLY_ALLOCATED: '#22c55e' }[k] || '#94a3b8',
  }));

  // Fab stage waterfall data
  const fabStages = [
    { name: 'Fit-Up', ag: ag.fitUpInchDia || 0, ug: ug.fitUpInchDia || 0 },
    { name: 'Welding', ag: ag.weldingInchDia || 0, ug: ug.weldingInchDia || 0 },
    { name: 'NDT', ag: ag.ndtInchDia || 0, ug: ug.ndtInchDia || 0 },
    { name: 'Painting', ag: ag.paintingInchDia || 0, ug: ug.paintingInchDia || 0 },
    { name: 'Dispatch', ag: ag.dispatchInchDia || 0, ug: ug.dispatchInchDia || 0 },
  ];

  return (
    <div className="space-y-4" data-testid="pwm-analytics">
      {/* AG/UG Summary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[{ key: 'AG', data: ag, color: '#3b82f6' }, { key: 'UG', data: ug, color: '#f59e0b' }].map(({ key, data, color }) => (
          <div key={key} className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <h4 className="text-sm font-bold mb-3" style={{ color }}>{key === 'AG' ? 'Above Ground (AG)' : 'Underground (UG)'}</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]">
              <div><span style={{ color: 'var(--text-muted)' }}>Lines in WF:</span><p className="font-bold" style={{ color: 'var(--text-primary)' }}>{data.linesInWorkfront} / {data.totalLines}</p></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Total Line ID:</span><p className="font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{formatIndian(data.totalLineInchDia)}</p></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Total Spool ID:</span><p className="font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{formatIndian(data.totalSpoolInchDia)}</p></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Hard Alloc ID:</span><p className="font-bold font-mono" style={{ color: '#22c55e' }}>{formatIndian(data.hardAllocatedInchDia)}</p></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Fabricated ID:</span><p className="font-bold font-mono" style={{ color: '#143A6B' }}>{formatIndian(data.fabricatedInchDia)}</p></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Dispatched ID:</span><p className="font-bold font-mono" style={{ color: '#10b981' }}>{formatIndian(data.dispatchedInchDia)}</p></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Total Iso IM:</span><p className="font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{formatIndian(data.totalIsometricIM)}</p></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Erected IM:</span><p className="font-bold font-mono" style={{ color: '#143A6B' }}>{formatIndian(data.erectedIM)}</p></div>
            </div>
          </div>
        ))}
      </div>

      {/* KPI Row */}
      {fabKpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <KPICard label="Total Spool ID" value={formatIndian(fabKpis.totalSpoolInchDia)} icon={Package} color="#143A6B" />
          <KPICard label="Earned ID" value={formatIndian(fabKpis.earnedInchDia)} sub={`${fabKpis.overallPct?.toFixed(1)}%`} icon={Activity} color="#10b981" />
          <KPICard label="Dispatched ID" value={formatIndian(fabKpis.dispatchedInchDia)} icon={TrendingUp} color="#22c55e" />
          <KPICard label="Fit-Up Rate" value={`${fabKpis.fitUpRate?.toFixed(1)}%`} icon={Gauge} color="#3b82f6" />
          <KPICard label="NDT Clearance" value={`${fabKpis.ndtClearanceRate?.toFixed(1)}%`} icon={Target} color="#143A6B" />
          <KPICard label="Disp Cycle" value={`${fabKpis.avgDispatchCycleDays} days`} sub="avg/spool" icon={Zap} color="#f59e0b" />
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Fabrication S-Curve */}
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h4 className="text-xs font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Fabrication S-Curve (Inch-Dia)</h4>
          {sCurve.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={sCurve}>
                <defs>
                  <linearGradient id="pwmFabGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#143A6B" stopOpacity={0.3} /><stop offset="95%" stopColor="#143A6B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="label" tick={{ fontSize: 8, fill: tickColor }} interval="preserveStartEnd" angle={-45} textAnchor="end" height={40} />
                <YAxis tick={{ fontSize: 9, fill: tickColor }} tickFormatter={v => `${v}%`} domain={[0, 'auto']} />
                <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 10 }} />
                <Legend wrapperStyle={{ fontSize: '10px' }} />
                <Area type="monotone" dataKey="totalPct" stroke="#143A6B" fill="url(#pwmFabGrad)" strokeWidth={2} name="Total %" />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <div className="h-48 flex items-center justify-center text-sm" style={{ color: 'var(--text-muted)' }}>No data</div>}
        </div>

        {/* Erection S-Curve */}
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h4 className="text-xs font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Erection S-Curve (Inch-Meter)</h4>
          {erecSCurve.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={erecSCurve}>
                <defs>
                  <linearGradient id="pwmErecGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} /><stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="label" tick={{ fontSize: 8, fill: tickColor }} interval="preserveStartEnd" angle={-45} textAnchor="end" height={40} />
                <YAxis tick={{ fontSize: 9, fill: tickColor }} tickFormatter={v => `${v}%`} domain={[0, 'auto']} />
                <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 10 }} />
                <Legend wrapperStyle={{ fontSize: '10px' }} />
                <Area type="monotone" dataKey="totalPct" stroke="#F59E0B" fill="url(#pwmErecGrad)" strokeWidth={2} name="Total %" />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <div className="h-48 flex items-center justify-center text-sm" style={{ color: 'var(--text-muted)' }}>No data</div>}
        </div>
      </div>

      {/* Bottom Row: Stage Waterfall + State Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Fab Stage Waterfall */}
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h4 className="text-xs font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Fabrication Stage Waterfall (Inch-Dia)</h4>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={fabStages}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: tickColor }} />
              <YAxis tick={{ fontSize: 9, fill: tickColor }} />
              <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 10 }}
                formatter={(v) => formatIndian(v)} />
              <Legend wrapperStyle={{ fontSize: '10px' }} />
              <Bar dataKey="ag" name="AG" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="ug" name="UG" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Allocation State Pie */}
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h4 className="text-xs font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Allocation State Distribution</h4>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={stateData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} dataKey="value">
                {stateData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 mt-2 justify-center">
            {stateData.map(s => (
              <span key={s.name} className="flex items-center gap-1 text-[9px]" style={{ color: 'var(--text-muted)' }}>
                <span className="w-2 h-2 rounded-full" style={{ background: s.fill }} /> {s.name}: {s.value}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
